'use client';

import { useEffect, useRef, useState } from 'react';
import { isAppEnvironment } from '@/lib/fcm';

/**
 * 应用内更新检测（v1.0 正式发布包）：
 * App 环境启动 3s 后拉取 GET /api/v1/app/version，
 * 与本地 Capacitor App.getInfo().version 比对，发现新版本弹出更新提示条。
 * 点击「更新」用系统浏览器打开下载页（APK 安装包经 CDN 部署包通道分发）。
 * 非 App 环境 / 后端未配置版本时静默跳过。
 */
interface VersionResp {
  version: string | null;
  versionCode: number | null;
  url: string | null;
  mandatory: boolean;
  changelog: Record<string, string> | null;
}

function appGetInfo(): Promise<{ version?: string } | null> {
  return new Promise((resolve) => {
    try {
      const AppPlugin = window.Capacitor?.Plugins?.App as
        | { getInfo?: () => Promise<{ version?: string }> }
        | undefined;
      if (!AppPlugin?.getInfo) return resolve(null);
      AppPlugin.getInfo().then(resolve).catch(() => resolve(null));
    } catch {
      resolve(null);
    }
  });
}

export default function UpdateChecker() {
  const [notice, setNotice] = useState<VersionResp | null>(null);
  const checkedRef = useRef(false);
  const [zh, setZh] = useState(true);

  useEffect(() => {
    try {
      const obj = JSON.parse(localStorage.getItem('AiLumix-store') || '{}');
      setZh(obj?.state?.language !== 'en');
    } catch {
      setZh(false);
    }
  }, []);

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    let timer: number;

    const check = async () => {
      if (!isAppEnvironment()) return;
      const info = await appGetInfo();
      const local = String(info?.version ?? '');
      if (!local) return;
      try {
        const r = await fetch('/api/v1/app/version', { cache: 'no-store' });
        const j = (await r.json()) as VersionResp;
        if (!j?.version || !j.url) return;
        // 仅当线上版本与本地不一致时提示（不同则说明有新包）
        if (j.version === local) return;
        setNotice(j);
      } catch {
        /* 忽略瞬时错误 */
      }
    };

    timer = window.setTimeout(check, 3000);
    return () => window.clearTimeout(timer);
  }, []);

  if (!notice) return null;
  const changelogText = notice.changelog ? (notice.changelog[zh ? 'zh' : 'en'] ?? '') : '';

  return (
    <div className="fixed bottom-24 left-1/2 z-[99] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 sm:bottom-6">
      <div className="rounded-2xl border border-gf-accent/40 bg-gf-surface2/95 p-4 shadow-2xl backdrop-blur">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">
              {zh ? `发现新版本 v${notice.version}` : `New version v${notice.version} available`}
            </div>
            {changelogText && (
              <div className="mt-1 text-xs leading-relaxed text-gf-muted">{changelogText}</div>
            )}
            {notice.mandatory && (
              <div className="mt-1 text-[10px] font-medium text-gf-negative">
                {zh ? '此版本为强制更新' : 'This update is mandatory'}
              </div>
            )}
          </div>
          <div className="flex shrink-0 gap-2">
            {!notice.mandatory && (
              <button
                onClick={() => setNotice(null)}
                className="rounded-full border border-gf-border px-3 py-1.5 text-xs text-gf-muted"
              >
                {zh ? '稍后' : 'Later'}
              </button>
            )}
            <button
              onClick={() => {
                if (notice.url) window.open(notice.url, '_system');
              }}
              className="rounded-full bg-gf-accent px-3 py-1.5 text-xs font-semibold text-white"
            >
              {zh ? '立即更新' : 'Update now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
